import InfoTooltip from "@/components/UI/InfoTooltip";
import { determineNotationCurrencySymbol } from "@/lib/currencySymbolHelper";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";
import featureFlagStore from "@/store/featureFlagStore";
import useTableUIStore from "@/store/tableUIStore";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore, {
  ITradeSettingsStore,
} from "@/store/tradeSettingsStore";
import { clearAllCPSettingOverrides } from "@/store/tradeSettingsStoreACTIONS";
import {
  ICPSettingsOverride,
  IToggleButton,
  IToggleButtonToReturnBoolean,
  IToggleCounterpartiesButton,
} from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";
import {
  SINGLE_CP_COVER_TOOLTIP,
  isCoverSettingLockedBySingleCP,
} from "../../helper/tradeSettingsHelper/coverSettingsRule";
import { isSideLockedToOneWay } from "../../helper/tradeSettingsHelper/unwindSideRule";
import { useNPVSideLock } from "../../helper/tradeSettingsHelper/useNPVSideLock";
import {
  TRADE_SETTINGS_CONTROL_HEIGHT,
  getFontSizeClass,
} from "../../helper/actionSummaryHelper";
import { Tooltip } from "react-tooltip";
import { resolveSettingForCP } from "./SelectPanelHelper";
import { useEffect, useState } from "react";
import clsx from "clsx";
import {
  cpHasOverrideOrPendingChange,
  isExpiryTypeOptionDisabled,
  isTradeLevelFieldPending,
  isTradeWideFieldPending,
} from "./CPSettingsGridHelper";
import {
  ICEBERG_AT_MAX_TOOLTIP,
  isTradeLevelIcebergLockedAt100,
} from "./CPSettingsGridIcebergHelpers";
import {
  COLUMN_WIDTHS_BY_FONT_SIZE,
  GAP,
  ICPGridSettingConfig,
} from "./CPSettingsGridConstants";
export type { FontSizeSetting } from "./CPSettingsGridConstants";
export { COLUMN_WIDTHS_BY_FONT_SIZE } from "./CPSettingsGridConstants";
import { SettingDropdown } from "./SettingDropdown";
export { SettingDropdown };
import { SettingNumericInput } from "./SettingNumericInput";
export { SettingNumericInput };
import { OrderSetLevelRow } from "./OrderSetLevelRow";
import { CPOverrideRows } from "./CPOverrideRows";
import { ExpiryDurationInput } from "./ExpiryDurationInput";
import ExpiryCountdownText from "../../helper/cellRenderers/MainTableActionSummaryESLevel/ExpiresCellRenderer/ExpiryCountdownText";

interface ICPSettingsGrid {
  esid: string;
  data: ExecutionStrategy;
  containsClose: boolean;
  isEditPanel?: boolean;
}

// Sync orderPriceValue to leg referencePrice for Outright trades
export function syncPriceToLeg(
  esid: string,
  dataStrategyType: string,
  value: number | string | undefined,
) {
  if (dataStrategyType !== "Outright") return;

  const currentTradeData = tradeDataStore.getState().tradeData;
  const thisTrade = currentTradeData.find((trade) => trade.esid === esid);

  if (!thisTrade || !thisTrade.legs || thisTrade.legs.length === 0) return;

  const newLegs = thisTrade.legs.map((leg, index) =>
    index === 0 ? { ...leg, referencePrice: value } : leg,
  );

  const newThisTrade = { ...thisTrade, legs: newLegs };
  const newData = currentTradeData.map((trade) =>
    trade.esid === esid ? newThisTrade : trade,
  );

  tradeDataStore.getState().updateTradeData(newData);
}

const CPSettingsGrid = ({
  esid,
  data,
  containsClose,
  isEditPanel,
}: ICPSettingsGrid) => {
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const fontSizeClass = getFontSizeClass(fontSizeSetting);
  const COLUMN_WIDTHS = COLUMN_WIDTHS_BY_FONT_SIZE[fontSizeSetting];

  const thisTradeSettings = tradeSettingsStore(
    (state) => state.tradeSettingsData.find((t) => t.esid === esid)?.settings,
  );
  // Baseline for "saved vs pending" colouring on CP Overrides rows (absent on
  // New trades).
  const editBaseline = tradeSettingsStore(
    (state) => state.editBaselineOverrides[esid],
  );
  // Trade-wide baseline (autoAffirm, releaseCoverSpread) — absent on New trades.
  const tradeWideBaseline = tradeSettingsStore(
    (state) => state.editTradeWideBaselineByEsid[esid],
  );
  // Trade-level baseline for CP-overridable defaults (Side, Direction, Iceberg,
  // Advise Comp, Advise Cover) — absent on New trades. Drives the trade-level
  // pending orange and the muted "following" orange on CPs without an override.
  const tradeLevelBaseline = tradeSettingsStore(
    (state) => state.editTradeLevelBaselineByEsid[esid],
  );
  // LQC-1040: CP codes returned by the GET response (extended after PUT).
  // Drives the iceberg floor rule — CPs not in this list are brand-new in
  // this edit session and have no floor.
  const knownCpCodes = tradeSettingsStore(
    (state) => state.editKnownCpCodes[esid],
  );
  // LQC-1938 - backend-driven flag re-enabling Iceberg (releaseSize) for
  // Orders. The other Order restrictions (Side, release*) are NOT flag-gated.
  const icebergForOrders = featureFlagStore((state) => state.icebergForOrders);
  // LQC-2170 - backend flags gating the Day/Time expiry options
  // ("ExpiryTypeDayFeature" / "ExpiryTypeTimeFeature"). Columns always render.
  const expiryTypeDay = featureFlagStore((state) => state.expiryTypeDay);
  const expiryTypeTime = featureFlagStore((state) => state.expiryTypeTime);

  // CP display mode toggle. "All" = single trade-level row applies to every CP;
  // "Custom" = render an editable row for every active CP. In edit mode we
  // auto-flip to "Custom" once the API hydration has seeded any CP overrides
  // so the user immediately sees the existing customisation.
  const [cpDisplayMode, setCpDisplayMode] = useState<"All" | "Custom">("All");

  useEffect(() => {
    if (
      isEditPanel &&
      thisTradeSettings &&
      Object.values(thisTradeSettings.cpSettingsOverrides).some(
        (override) => Object.keys(override).length > 0,
      )
    ) {
      setCpDisplayMode("Custom");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditPanel, thisTradeSettings?.cpSettingsOverrides]);

  // Get notation for Order level input (mirrors SelectPanelSettings notation resolution)
  const rawNotation = (data?.notationES || undefined) as
    | NotationToDecimalValueType
    | undefined;
  const tradeData = tradeDataStore((state) => state.tradeData);
  const currentStoreTrade = tradeData.find(
    (trade: ExecutionStrategy) => trade.esid === esid,
  );
  const notation = currentStoreTrade
    ? determineNotationCurrencySymbol(currentStoreTrade, rawNotation)
    : rawNotation;
  // Coerce the optional StrategyType enum to a string — downstream consumers
  // (OrderSetLevelRow, syncPriceToLeg) take a string, and an empty string
  // matches neither StrategyType.Structure nor "Outright", which is the correct
  // no-op behaviour when a trade has no strategyType.
  const dataStrategyType: string = data?.strategyType ?? "";

  // NPV forces One-Way + Show direction. Recoupon does not - see unwindSideRule.
  const NPVLegInTrade = useNPVSideLock(esid);

  if (!thisTradeSettings) return null;

  const activeCounterparties = thisTradeSettings.counterpartiesList.filter(
    (cp: IToggleCounterpartiesButton) => cp.active,
  );

  // Trade-type source depends on the mode this grid is rendered in (mirrors the
  // render gate: status === New || isEditPanel):
  //  • New trade (Select Panel): the user picks RFQ/Order via the "Send as"
  //    toggle, which writes to settings.tradeTypes. The trade isn't sent yet, so
  //    data.type does NOT reflect the toggle — settings.tradeTypes is the only
  //    authoritative source here.
  //  • Edit Panel (post-send): the type is fixed, so read the authoritative
  //    trade data (data.type). settings.tradeTypes is hydrated from the
  //    Edit/Settings API's optional `requestType` and defaults to RFQ when
  //    absent, so on a cold (post-refresh) store an Order would be misread as
  //    RFQ — this matches patchSidebarTradeSettings' determineIsOrder(trade).
  const isOrder = isEditPanel
    ? data?.type === "Order"
    : thisTradeSettings.tradeTypes.find((t) => t.active)?.name === "Order";

  // Iceberg is locked for Orders unless the IcebergForOrders feature is on.
  const icebergDisabledForOrder = isOrder && !icebergForOrders;

  // Side (quoteType) dropdown is disabled when:
  //   - trade contains a Close leg or an NPV leg (both forced to One-Way)
  //   - trade type is Order — Orders are always One-Way (PROD parity; the
  //     dropdown drift from the toggle->dropdown migration meant Orders could
  //     be set Two-Way in the UI even though the backend rejects it)
  const sideDisabled = isSideLockedToOneWay({
    containsClose,
    containsNPV: NPVLegInTrade,
    isOrder,
  });

  // A CP "shows a border" — orange (pending vs baseline) or green (saved) —
  // when it has a current override OR a baseline entry. Drives both the Custom
  // grid visibility and the "All" lock so they match the cell colouring. The
  // baseline half matters: a CP reverted to a default keeps an orange cell
  // (baseline still holds the old server value) and must stay locked until the
  // change is sent. LQC-1119.
  const anyActiveCpShowsBorder = activeCounterparties.some(
    (cp: IToggleCounterpartiesButton) =>
      cpHasOverrideOrPendingChange(
        thisTradeSettings.cpSettingsOverrides[cp.code],
        editBaseline?.[cp.code],
      ),
  );

  // LQC-1704: Cover (Advise Cover) and Cover Spread only apply when sending to
  // more than one CP. Lock both — trade-level row and per-CP override rows —
  // when a single (or no) CP is active. Orders are already disabled separately
  // (and always single-CP), so this is scoped to RFQs to avoid double-handling.
  const lockCoverBySingleCp =
    !isOrder && isCoverSettingLockedBySingleCP(activeCounterparties.length);

  // Order-specific: determine which Set Level and Level settings to use
  const isStructure = dataStrategyType === StrategyType.Structure;
  const orderSetLevel = isStructure
    ? thisTradeSettings.orderSetLevelStructure
    : thisTradeSettings.orderSetLevelOutright;
  const orderSetLevelField: keyof ITradeSettingsStore = isStructure
    ? "orderSetLevelStructure"
    : "orderSetLevelOutright";
  const orderValueField: keyof ITradeSettingsStore = isStructure
    ? "orderSpreadValue"
    : "orderPriceValue";
  const activeSetLevel = orderSetLevel?.find((t) => t.active)?.name;
  // Level input is disabled when Set Level is "Market"
  const orderLevelDisabled = activeSetLevel === "Market";

  // LQC-1092: hide the Set Level row entirely once an Order has been sent
  // (any status past New) — both the Market ↔ Leg/Spread toggle and the
  // Level/Spread numeric input. Switching order semantics post-send creates
  // backend states we can't reconcile.
  const showOrderSetLevelRow = data?.status === ExecutionStrategyStatus.New;

  const globalIsOneWay =
    thisTradeSettings.quoteType.find((t) => t.active)?.name === "One-Way";

  const activeExpiryType = thisTradeSettings.expiryType.find(
    (t) => t.active,
  )?.name;

  // CPs that carry an explicit override entry. NOTE: the Custom-mode grid below
  // renders a row for EVERY active CP (see the activeCounterparties.map ~line
  // 1190) — CPs without an override follow the trade-level default via
  // resolveSettingForCP. This list is used only for column force-show logic
  // (isColumnForceShownByCP) and the commented-out legend/empty-state messaging,
  // NOT to filter which CP rows render.
  const banksWithOverrides = activeCounterparties.filter(
    (cp: IToggleCounterpartiesButton) =>
      thisTradeSettings.cpSettingsOverrides[cp.code] !== undefined,
  );

  // ===== Settings configuration array =====
  // Comment out an object to remove it from the UI. Reorder to change column order.
  const settingsColumns: ICPGridSettingConfig[] = [
    // --- CP-overridable settings (also appear in CP Overrides per-bank rows) ---

    {
      settingField: "quoteType",
      heading: "Side",
      id: "quoteType",
      columnWidth: COLUMN_WIDTHS.setLevel,
      type: "dropdown",
      cpOverridable: true,
      disabled: sideDisabled,
    },
    {
      settingField: "releaseDirection",
      heading: "Direction",
      id: "releaseDirectionType",
      columnWidth: COLUMN_WIDTHS.side,
      type: "dropdown",
      cpOverridable: true,
      hidden: globalIsOneWay,
      isCPCellVisible: (cpCode) =>
        resolveSettingForCP(thisTradeSettings, cpCode, "quoteType").find(
          (t) => t.active,
        )?.name !== "One-Way",
    },
    {
      settingField: "releaseSize",
      heading: "Iceberg",
      id: "releaseSize",
      columnWidth: COLUMN_WIDTHS.iceberg,
      type: "numeric",
      cpOverridable: true,
      min: 1,
      max: 100,
      suffix: "%",
    },
    {
      settingField: "releaseCompetition",
      heading: "Advise Comp",
      id: "releaseCompetition",
      columnWidth: COLUMN_WIDTHS.adviseCompetition,
      type: "dropdown",
      cpOverridable: true,
    },
    {
      settingField: "releaseCover",
      heading: "Advise Cover",
      id: "releaseCover",
      columnWidth: COLUMN_WIDTHS.adviseCover,
      type: "dropdown",
      cpOverridable: true,
    },
    {
      // LQC-1705: Cover Spread is now per-CP Yes/No (was a trade-wide tri-state).
      settingField: "releaseCoverSpread",
      heading: "Cover Spread",
      id: "releaseCoverSpread",
      columnWidth: COLUMN_WIDTHS.adviseCover,
      type: "dropdown",
      cpOverridable: true,
    },
    // --- Global-only settings (not shown in CP Overrides) ---
    // KEEP THIS - Will be used in Peer to Peer
    // {
    //   settingField: "releaseCounterparty",
    //   heading: "Release CP",
    //   id: "releaseCounterparty",
    //   columnWidth: COLUMN_WIDTHS.adviseCompetition,
    //   type: "dropdown",
    //   cpOverridable: false,
    //   showInEditPanelAsReadOnly: true,
    //   // Hidden in v1 — peer-to-peer / dark pool starting point per Simon.
    //   // See SETTINGS_MATRIX.md "Hidden settings" section.
    //   hidden: true,
    // },
    {
      settingField: "autoAffirm",
      heading: "Auto Affirm",
      id: "autoAffirm",
      columnWidth: COLUMN_WIDTHS.autoAffirm,
      type: "dropdown",
      cpOverridable: false,
      showInEditPanel: true,
      infoMessage: isOrder
        ? "Automatically affirm trades 60 seconds after Filled (including No More Fills)"
        : "Automatically affirm trades 60 seconds after Executed",
    },
    // LQC-2170: GTC is the store default. Day/Time options render disabled
    // while their feature flag is off.
    {
      settingField: "expiryType",
      heading: "Expiry",
      id: "expiryType",
      columnWidth: COLUMN_WIDTHS.side,
      type: "dropdown",
      cpOverridable: false,
      infoMessage: (
        <div className="flex flex-col gap-1">
          <div>GTC: Good til cancelled</div>
          <div>Day: Midnight tonight</div>
          <div>Time: Specify duration matching the 'Time to Expiry' format</div>
        </div>
      ),
      infoMessageWidthClass: "max-w-[360px]",
    },
    {
      settingField: "expiryTimeValue",
      heading: "Time to Expiry",
      id: "expiryTimeValue",
      columnWidth: COLUMN_WIDTHS.adviseCover,
      type: "duration",
      cpOverridable: false,
      // Hidden, not disabled, for GTC/Day: an "NA" box read as "does not apply" (LQC-3265).
      hidden: activeExpiryType !== "Time",
    },
  ];

  // Derived column lists for rendering
  // Trade-global settings (cpOverridable: false) are normally hidden in edit
  // mode because the trade-level row resets to hardcoded defaults on every
  // hydration and would misrepresent the persisted value. Exceptions:
  //   showInEditPanel — hydrated from GET response, renders editable in Edit Panel.
  //   showInEditPanelAsReadOnly — hydrated from GET response, renders disabled in Edit Panel.
  // (See applyEditSettingsResponseToStore for the hydration source.)
  const isInEditMode = !!editBaseline;
  // A column that is globally hidden (e.g., Direction when the trade-level Side
  // is One-Way) must still be force-shown when any active CP with an override
  // would display it — otherwise the SETTINGS heading row loses a column and
  // drifts out of alignment with the per-CP rows below. Currently only
  // Direction uses isCPCellVisible.
  const isColumnForceShownByCP = (col: ICPGridSettingConfig): boolean =>
    !!col.isCPCellVisible &&
    banksWithOverrides.some((cp) => col.isCPCellVisible!(cp.code));
  const visibleSettings = settingsColumns.filter(
    (col) =>
      (!col.hidden || isColumnForceShownByCP(col)) &&
      (!isInEditMode ||
        col.cpOverridable ||
        col.showInEditPanel ||
        col.showInEditPanelAsReadOnly),
  );
  const cpOverridableSettings = settingsColumns.filter(
    (col) => col.cpOverridable,
  );
  // A CP column is visible in CP Overrides if it's not globally hidden,
  // or if any bank's cell would still be visible (e.g., Direction when some banks have non-One-Way Side)
  const visibleCPColumns = cpOverridableSettings.filter(
    (col) => !col.hidden || isColumnForceShownByCP(col),
  );

  return (
    <div
      data-testid="CPSettingsGridContainer"
      id="CPSettingsGridContainer"
      className="w-full py-1 pl-2"
    >
      {/* ===== SECTION 1: SETTINGS ===== */}
      <fieldset className="rounded border border-gray-300 px-3 pb-3 pt-1 dark:border-gray-600">
        {/* <legend
          className={`ml-2 px-1 uppercase tracking-wider text-gray-500 dark:text-gray-400 ${fontSizeClass}`}
        >
          Settings
        </legend> */}

        <div className={`flex flex-wrap items-start ${GAP}`}>
          {/* All / Custom toggle — first settings column. Drives whether per-CP
              override rows are shown below. For Orders (single CP) the
              dropdown is rendered but locked to "All" so the column layout
              stays consistent across trade types. Order iceberg
              (icebergForOrders ON) is authored on the trade-level row - per-CP
              rows stay Order-locked (LQC-1938). */}
          {(() => {
            // In edit mode, lock out "All" while any active CP shows an override
            // border (orange pending or green saved) — switching back to All
            // would discard server state or silently mask an unsent change. Only
            // once every CP is fully back in line with the server (no border —
            // overrides cleared AND the change sent via Update so the baseline
            // advances) does "All" unlock again. LQC-1119.
            const allOptionLocked =
              isEditPanel && !!thisTradeSettings && anyActiveCpShowsBorder;
            const allLockedTooltipId = `cpDisplayModeAllLocked-${esid}`;
            return (
              <div
                data-testid="cpDisplayModeToggle"
                id="cpDisplayModeToggle"
                className={COLUMN_WIDTHS.cpMode}
                data-tooltip-id={
                  allOptionLocked ? allLockedTooltipId : undefined
                }
              >
                <div
                  className={`mb-1 whitespace-nowrap uppercase tracking-wider text-gray-500 dark:text-gray-400 ${fontSizeClass}`}
                >
                  SETTINGS {/* CPs */}
                </div>
                <select
                  className={clsx(
                    "h-[28px] w-[110px] rounded-md border border-gray-300 bg-transparent px-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200",
                    fontSizeClass,
                    isOrder && "cursor-not-allowed opacity-60",
                  )}
                  disabled={isOrder}
                  value={isOrder ? "All" : cpDisplayMode}
                  onChange={(e) => {
                    const newMode = e.target.value as "All" | "Custom";
                    setCpDisplayMode(newMode);
                    // New trades: switching back to "All" wipes any per-CP
                    // edits so every CP picks up the current trade-level
                    // defaults via fallback. Edit mode preserves overrides —
                    // they may have been seeded from the API and silently
                    // discarding them would lose server state the user didn't
                    // explicitly touch.
                    if (newMode === "All" && !isEditPanel) {
                      clearAllCPSettingOverrides({ esid });
                    }
                    e.target.blur();
                  }}
                >
                  <option value="All" disabled={allOptionLocked}>
                    All
                  </option>
                  <option value="Custom">Custom</option>
                </select>
                {allOptionLocked && (
                  <Tooltip
                    id={allLockedTooltipId}
                    place="top"
                    delayShow={100}
                    className="z-50 !text-xs"
                  >
                    "All" is unavailable while per-CP overrides exist — remove
                    the overrides and click 'Update'.
                  </Tooltip>
                )}
              </div>
            );
          })()}

          {visibleSettings.map((col) => {
            // Trade-level row has no green colouring (isOverridden stays
            // undefined). Orange (pending) applies in edit mode when the current
            // value diverges from its baseline: showInEditPanel fields
            // (autoAffirm / coverSpread) compare to the trade-wide baseline;
            // cpOverridable defaults compare to the trade-level baseline.
            const globalIsOverridden: boolean | undefined = undefined;
            const globalIsPending: boolean | undefined = (() => {
              if (col.showInEditPanel && tradeWideBaseline) {
                const settingArray = thisTradeSettings[col.settingField];
                if (!Array.isArray(settingArray)) return undefined;
                const baselineValue =
                  tradeWideBaseline[
                    col.settingField as keyof typeof tradeWideBaseline
                  ];
                return (
                  isTradeWideFieldPending(
                    settingArray as
                      | IToggleButton[]
                      | IToggleButtonToReturnBoolean[],
                    baselineValue,
                  ) || undefined
                );
              }
              if (col.cpOverridable && tradeLevelBaseline) {
                return (
                  isTradeLevelFieldPending(
                    thisTradeSettings,
                    tradeLevelBaseline,
                    col.settingField,
                  ) || undefined
                );
              }
              return undefined;
            })();

            // The four release* settings don't apply to Orders — they're
            // reset to defaults when type switches to Order (see
            // setActiveToggle) and shown disabled here.
            const disabledByOrder =
              isOrder &&
              (col.settingField === "releaseCompetition" ||
                col.settingField === "releaseCover" ||
                col.settingField === "releaseCoverSpread" ||
                col.settingField === "releaseCounterparty");
            // Trade-only fields whose persisted value is hydrated from the
            // GET response render disabled in Edit Panel (read-only display).
            const disabledByEditPanelReadOnly =
              isInEditMode && !!col.showInEditPanelAsReadOnly;
            // Direction is force-shown on the trade-level row when a CP override
            // diverges to Two-Way (see isColumnForceShownByCP). The trade-level
            // Side is still One-Way in that case, so its Direction has no
            // meaning — render it disabled rather than editable.
            const directionDisabledByOneWay =
              col.settingField === "releaseDirection" && globalIsOneWay;
            // LQC-1704: Cover (Advise Cover) and Cover Spread are locked with a
            // single CP — shows the tooltip prompting the user to add a CP.
            const lockedBySingleCp =
              lockCoverBySingleCp &&
              (col.settingField === "releaseCover" ||
                col.settingField === "releaseCoverSpread");
            const dropdownDisabled =
              col.disabled ||
              disabledByOrder ||
              disabledByEditPanelReadOnly ||
              directionDisabledByOneWay ||
              lockedBySingleCp;
            const singleCpTooltipId = lockedBySingleCp
              ? `coverSingleCp-${esid}-${col.id}`
              : undefined;

            return (
              <div
                key={col.id}
                data-testid={col.id}
                id={col.id}
                className={col.columnWidth}
              >
                <div
                  className={clsx(
                    "mb-1 whitespace-nowrap tracking-wider text-gray-500 dark:text-gray-400",
                    fontSizeClass,
                    col.infoMessage && "flex items-center",
                  )}
                >
                  {col.heading}
                  {col.infoMessage && (
                    <InfoTooltip
                      id={`cpGrid-${col.id}-info`}
                      message={col.infoMessage}
                      maxWidthClass={col.infoMessageWidthClass}
                    />
                  )}
                </div>
                {col.type === "dropdown" ? (
                  <SettingDropdown
                    settingArray={
                      thisTradeSettings[col.settingField] as
                        | IToggleButton[]
                        | IToggleButtonToReturnBoolean[]
                    }
                    esid={esid}
                    settingField={col.settingField}
                    fontSizeClass={fontSizeClass}
                    disabled={dropdownDisabled}
                    isOverridden={globalIsOverridden}
                    isPending={globalIsPending}
                    tooltipId={singleCpTooltipId}
                    tooltipMessage={
                      lockedBySingleCp ? SINGLE_CP_COVER_TOOLTIP : undefined
                    }
                    isOptionDisabled={
                      col.settingField === "expiryType"
                        ? (optionName) =>
                            isExpiryTypeOptionDisabled(optionName, {
                              expiryTypeDay,
                              expiryTypeTime,
                            })
                        : undefined
                    }
                  />
                ) : col.type === "duration" ? (
                  <ExpiryDurationInput
                    esid={esid}
                    valueSeconds={
                      thisTradeSettings[col.settingField] as number | undefined
                    }
                    fontSizeClass={fontSizeClass}
                  />
                ) : (
                  (() => {
                    const settingValue = thisTradeSettings[col.settingField] as
                      | number
                      | undefined;
                    // Iceberg is disabled on Orders unless the icebergForOrders
                    // flag is ON (LQC-1938). No tooltip when disabled,
                    // mirroring the Side dropdown's silent-disable behaviour
                    // for Orders. setActiveToggle forces releaseSize to 100
                    // when switching to Order (flag OFF), so the disabled cell
                    // always shows 100%.
                    const orderIcebergDisabled =
                      col.settingField === "releaseSize" &&
                      icebergDisabledForOrder;
                    // LQC-1040: trade-level iceberg cell is greyed out when
                    // the value is at 100% post-send — no valid edit exists.
                    const greyedOutByIcebergCap =
                      col.settingField === "releaseSize" &&
                      !orderIcebergDisabled &&
                      isTradeLevelIcebergLockedAt100(
                        settingValue,
                        editBaseline,
                      );
                    const disabled =
                      col.disabled ||
                      orderIcebergDisabled ||
                      greyedOutByIcebergCap;
                    return (
                      <SettingNumericInput
                        value={settingValue}
                        esid={esid}
                        settingField={col.settingField}
                        fontSizeClass={fontSizeClass}
                        min={col.min}
                        max={col.max}
                        suffix={col.suffix}
                        isOverridden={globalIsOverridden}
                        isPending={globalIsPending}
                        disabled={disabled}
                        tooltipId={
                          greyedOutByIcebergCap
                            ? `tradeLevelIcebergLocked-${esid}`
                            : undefined
                        }
                        tooltipMessage={
                          greyedOutByIcebergCap
                            ? ICEBERG_AT_MAX_TOOLTIP
                            : undefined
                        }
                      />
                    );
                  })()
                )}
              </div>
            );
          })}

          {/* LQC-2678 - read-only time remaining. Edit Panel only: a New trade
              has no expiryEnd until it is sent, so there is nothing to count
              down. Deliberately the live countdown off expiryEnd, not the
              expiryTimeValue setting beside it - the setting is the duration
              the OP originally picked, which never answers "how long is left".
              Editing stays on the Expires column's pencil / right-click. */}
          {isEditPanel && (
            <div
              data-testid="cpGridExpiresReadOnly"
              id="cpGridExpiresReadOnly"
              className={COLUMN_WIDTHS.adviseCover}
            >
              <div
                className={clsx(
                  "mb-1 whitespace-nowrap tracking-wider text-gray-500 dark:text-gray-400",
                  fontSizeClass,
                )}
              >
                Expires
              </div>
              <div
                className={clsx(
                  "flex items-center",
                  TRADE_SETTINGS_CONTROL_HEIGHT,
                  fontSizeClass,
                )}
              >
                <ExpiryCountdownText
                  esid={esid}
                  testId="CPSettingsGridExpiresValue"
                />
              </div>
            </div>
          )}
        </div>

        {/* ===== Order-specific: Set Level + Level input on a second row ===== */}
        {/* LQC-1092: hidden once the Order has been sent (status past New). */}
        {isOrder && orderSetLevel && showOrderSetLevelRow && (
          <OrderSetLevelRow
            esid={esid}
            isStructure={isStructure}
            orderSetLevel={orderSetLevel}
            orderSetLevelField={
              orderSetLevelField as keyof ICPSettingsOverride &
                keyof ITradeSettingsStore
            }
            orderValueField={orderValueField}
            orderLevelDisabled={orderLevelDisabled}
            storeValue={
              thisTradeSettings[orderValueField] as number | string | undefined
            }
            notation={notation as NotationToDecimalValueType | undefined}
            fontSizeClass={fontSizeClass}
            setLevelWidth={COLUMN_WIDTHS.setLevel}
            adviseCoverWidth={COLUMN_WIDTHS.adviseCover}
            gap={GAP}
            dataStrategyType={dataStrategyType}
          />
        )}
        {/* ===== SECTION 2: CP OVERRIDES — now lives inside the same Settings
            fieldset so a single border wraps the whole thing. The previous
            chevron-expanded inner fieldset, "CP Overrides" legend, and the
            "Select a CP / + Add All" controls are commented out below in case
            we want to revert. Hidden for Orders. */}
        {!isOrder &&
          cpDisplayMode === "Custom" &&
          activeCounterparties.length > 0 && (
            <div className="mt-3 border-t border-gray-200 pt-2 dark:border-gray-700">
              <CPOverrideRows
                esid={esid}
                activeCounterparties={activeCounterparties}
                visibleCPColumns={visibleCPColumns}
                thisTradeSettings={thisTradeSettings}
                editBaseline={editBaseline}
                tradeLevelBaseline={tradeLevelBaseline}
                knownCpCodes={knownCpCodes}
                fontSizeClass={fontSizeClass}
                bankWidth={COLUMN_WIDTHS.bank}
                removeWidth={COLUMN_WIDTHS.remove}
                gap={GAP}
                lockCoverBySingleCp={lockCoverBySingleCp}
              />
            </div>
          )}
      </fieldset>
    </div>
  );
};

export default CPSettingsGrid;
